import { ChatMessage } from './ChatBotContextProvider';

/**
 * Where a card belongs in the transcript.
 *
 * HQ-11. A live card goes on the end, because that is where the conversation is. A replayed one
 * is arriving after the whole transcript has already been restored, so appending would leave
 * every map and button from a long conversation piled up beneath it, out of order and detached
 * from the answers they came with.
 *
 * `afterTurn` is how many turns had been said when the card was first drawn, recorded by Daive as
 * it was sent. Counting turns rather than entries is what makes it survive the round trip: the
 * transcript comes back as turns only, and the cards are inserted into it afterwards, so an index
 * over all entries would mean something different on each side.
 */

/** The transcript entries that count as a turn: what somebody actually said. */
const TURN_TYPES: ReadonlyArray<ChatMessage['type']> = ['user', 'operator'];

export function isTurn(message: ChatMessage): boolean {
    return TURN_TYPES.includes(message.type);
}

/**
 * The index a replayed card should be inserted at.
 *
 * @param messages the transcript as it stands
 * @param afterTurn how many turns had been said when the card was drawn
 * @return the index to splice at; the end when the card belongs after everything
 */
export function insertionPoint(messages: ChatMessage[], afterTurn: number): number {
    let turns: number = 0;

    for (let index: number = 0; index < messages.length; index++) {
        if (isTurn(messages[index])) {
            turns++;
            if (turns > afterTurn) {
                return index;
            }
        }
    }

    return messages.length;
}

/**
 * The transcript with one card added, live or replayed.
 *
 * @param messages the transcript as it stands
 * @param entry the card
 * @param afterTurn the card's recorded position, or undefined for a live card
 * @return a new transcript
 */
export function withCard(
    messages: ChatMessage[],
    entry: ChatMessage,
    afterTurn?: number
): ChatMessage[] {
    const next: ChatMessage[] = [...messages];

    if (typeof afterTurn !== 'number') {
        next.push(entry);
        return next;
    }

    next.splice(insertionPoint(next, afterTurn), 0, entry);

    return next;
}
